/**
 * The single-length orientation rule, in one place.
 *
 * Single-length mode: clients author and assign `Default` only. The timed
 * variants (Min5/Min10/Min20/Min60) are dormant, reserved for the planned AI
 * generator (AGENT-AUTHORED-ORIENTATIONS-PLAN). Do not write timed rows until
 * that lands.
 *
 * Two consequences the portal cares about, both owned here:
 *   - Everything we WRITE (assignments, orientation defaults, scripts) carries
 *     `{ tag: 'Default' }`.
 *   - Everything we DISPLAY hides `Default` (there is no length to choose any
 *     more) but still shows a legacy timed tag as-is, so an assignment created
 *     before this cutover keeps explaining itself.
 */

import type { OrientationVersion } from '@/generated/minetwin/types';

/** The only version this client ever writes. */
export const DEFAULT_VERSION: OrientationVersion = { tag: 'Default' } as OrientationVersion;

/**
 * Human label for an assignment's version tag, or `null` when there is nothing
 * worth showing. `Default` (the single authored script) renders nothing; a
 * legacy timed tag renders as its minute label.
 */
export function formatLegacyVersionTag(tag: string): string | null {
  return /^Min\d+$/.test(tag) ? `${tag.slice(3)} min` : null;
}

/**
 * Arguments for `assign_orientation`. Always `Default` — the length picker is
 * gone (see the rule above).
 */
export function assignOrientationArgs(
  email: string,
  orientationId: string,
  projectId: string,
): {
  email: string;
  orientationId: string;
  projectId: string;
  orientationVersion: OrientationVersion;
} {
  return { email, orientationId, projectId, orientationVersion: DEFAULT_VERSION };
}
